import { BEHAVIORS, STRENGTHS, WINDOW_MINUTES, isReviewer, kstDue } from './completeProject';
import { addWorkingMinutes } from './time';

/** 목 보드용 Complete project 상태 시나리오 */

const kst = (day, hh, mm = 0) => new Date(Date.UTC(2026, 9, day, hh - 9, mm)).toISOString();

export const PEER_SCENARIOS = [
  { key: 'locked', label: 'Locked' },
  { key: 'offline', label: 'Open · offline' },
  { key: 'on-time', label: 'Open · on time' },
  { key: 'late', label: 'Open · late' },
  { key: 'reset', label: 'Deadline reset' },
  { key: 'submitted', label: 'Submitted on time' },
  { key: 'submitted-late', label: 'Submitted late' },
  { key: 'closed', label: 'Closed' },
];

const FINAL_TASK = 't10';
const FIRST_SUBMIT = kst(17, 7);

const SCENES = {
  locked: { now: null, submits: [] },
  offline: { now: kst(17, 7, 20), submits: [FIRST_SUBMIT] },
  'on-time': { now: kst(17, 8, 20), submits: [FIRST_SUBMIT] },
  late: { now: kst(17, 11, 40), submits: [FIRST_SUBMIT] },
  reset: { now: kst(17, 8, 50), submits: [FIRST_SUBMIT, kst(17, 8, 30)] },
  submitted: { now: kst(17, 10, 20), submits: [FIRST_SUBMIT], mine: { submittedAt: kst(17, 10, 15), late: false } },
  'submitted-late': {
    now: kst(17, 13, 10),
    submits: [FIRST_SUBMIT],
    mine: { submittedAt: kst(17, 13, 5), late: true },
  },
  closed: { now: kst(18, 10, 30), submits: [FIRST_SUBMIT] },
};

const scores = (arr) => Object.fromEntries(BEHAVIORS.map((b, i) => [b.key, arr[i] ?? null]));
const strength = (i) => STRENGTHS[i].key;

const FULL = {
  mp: {
    scores: scores([4, 3, 4, 3, 3]),
    strength: strength(0),
    praise: 'Kept the scope clear and answered spec questions fast.',
    improve: 'Share copy changes earlier so design doesn’t wait.',
    thanks: 'Thanks for the quick calls on scope.',
    workAgain: 'yes',
  },
  ar: {
    scores: scores([3, 4, 3, 4, 'na']),
    strength: strength(3),
    praise: 'Built the flow exactly from the specs and flagged gaps early.',
    improve: 'Ask for missing states before starting, not after.',
    thanks: '',
    workAgain: 'yes',
  },
  dk: {
    scores: scores([3, 2, 3, 4, 2]),
    strength: strength(4),
    praise: 'Turned feedback around within an hour.',
    improve: 'Flag blockers in the thread instead of waiting for check-ins.',
    thanks: '',
    workAgain: 'yes',
  },
};
const PART = { scores: scores([3, 2]), strength: null, praise: '', improve: '', thanks: '', workAgain: null };

const draftFor = (key, mates) => {
  if (key === 'locked' || key === 'closed') return null;
  const done = key === 'submitted' || key === 'submitted-late';
  const chapters = {};
  mates.forEach((m, i) => {
    const full = FULL[m.id] ?? FULL.mp;
    chapters[m.id] = done || i < mates.length - 1 ? full : PART;
  });
  return {
    chapters,
    retro: done
      ? {
          learned: 'Handoffs go faster when the receiver sees the checklist first.',
          nextGoal: 'Lead a design review on my next team project.',
        }
      : { learned: '', nextGoal: '' },
    kit: done
      ? { picks: [{ type: 'checkin', id: 'cp3' }, { type: 'handoff', id: 't3' }], decided: true }
      : { picks: [], decided: false },
  };
};

export const withPeerScenario = (base, key, viewerId = base.viewerId) => {
  const scene = SCENES[key];
  if (!scene) return { ...base, viewerId };
  const leader = base.members.find((m) => m.role === 'leader');
  const reviewers = base.members.filter(isReviewer);
  const closesAt = new Date(new Date(base.project.endAt).getTime() + 24 * 3_600_000).toISOString();
  const last = scene.submits[scene.submits.length - 1] ?? null;
  const opensAt = scene.submits[0] ?? base.project.endAt;

  const tasks = base.tasks.map((t) =>
    t.id === FINAL_TASK && last
      ? {
          ...t,
          state: 'submitted',
          version: scene.submits.length,
          deliverable: {
            link: 'https://example.com/qa-report',
            summary: 'Every customer → rider path tested. Two known bugs listed with steps.',
            submittedAt: last,
          },
        }
      : t,
  );

  const byMember = {};
  reviewers.forEach((m) => {
    const mates = reviewers.filter((x) => x.id !== m.id);
    const sorted = [...mates].sort((a, b) => (b.role === 'leader') - (a.role === 'leader'));
    byMember[m.id] = {
      draft: draftFor(key, sorted),
      savedAt: scene.now ? new Date(new Date(scene.now).getTime() - 2 * 60000).toISOString() : null,
      ...(m.id === 'lt' ? scene.mine : null),
    };
  });

  const notifications = [...(base.notifications ?? [])];
  if (scene.submits.length) {
    reviewers.forEach((m) => {
      const mine = m.id === leader?.id;
      notifications.unshift({
        id: `pr-open-${m.id}`,
        userId: m.id,
        text: `${mine ? 'You submitted' : `${leader.name} (leader) submitted`} the final deliverable. Peer review is open.`,
        link: { to: 'complete-project' },
        createdAt: scene.submits[0],
        readAt: null,
      });
      if (scene.submits.length > 1) {
        const due = addWorkingMinutes(last, WINDOW_MINUTES, m);
        notifications.unshift({
          id: `pr-reset-${m.id}`,
          userId: m.id,
          text: `${mine ? 'You resubmitted' : `${leader.name} (leader) resubmitted`} the final deliverable. Your peer review deadline moved to ${kstDue(due)}.`,
          link: { to: 'complete-project' },
          createdAt: last,
          readAt: null,
        });
      }
    });
  }

  return {
    ...base,
    viewerId,
    frozenNow: scene.now ?? base.frozenNow,
    tasks,
    notifications,
    completeProject: {
      eligible: true,
      trigger: scene.submits.length ? 'final' : 'project_end',
      opensAt,
      openedById: scene.submits.length ? tasks.find((t) => t.id === FINAL_TASK)?.assigneeId : null,
      windowStartAt: last ?? base.project.endAt,
      reset: scene.submits.length > 1,
      closesAt,
      byMember,
    },
  };
};

export const peerScenarioKey = (v) => (PEER_SCENARIOS.some((s) => s.key === v) ? v : null);
